import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  CheckCircle,
  Clock,
  Building2,
  User,
  Calendar,
  DollarSign,
  MapPin,
  Phone,
  Mail,
  FileText,
  TrendingUp
} from 'lucide-react'
import { useVendorDashboard } from '../VendorDashboard'

const VendorPlacements = () => {
  const { isLoading } = useVendorDashboard()

  // Mock data - replace with actual API data
  const [placements] = useState([
    {
      id: 'PL-2024-001',
      poId: 'PO-2024-002',
      companyName: 'Digital Solutions Ltd.',
      resourceName: 'Sarah Johnson',
      resourceId: 2,
      position: 'Full Stack Developer',
      status: 'active',
      startDate: '2024-01-20',
      endDate: '2024-07-20',
      duration: '6 months',
      monthlyRate: 120000,
      totalValue: 720000,
      companyContact: {
        name: 'Mike Brown',
        email: 'mike.brown@digitalsolutions.com',
        phone: '+91 9876543211'
      },
      location: 'Mumbai, India',
      projectDescription: 'Full stack development for e-commerce platform'
    },
    {
      id: 'PL-2024-002',
      companyName: 'Tech Innovations Inc.',
      resourceName: 'Rajesh Kumar',
      resourceId: 5,
      position: 'Senior Python Developer',
      status: 'active',
      startDate: '2024-01-15',
      endDate: '2024-04-15',
      duration: '3 months',
      monthlyRate: 100000,
      totalValue: 300000,
      companyContact: {
        name: 'Priya Sharma',
        email: 'priya.sharma@techinnovations.com',
        phone: '+91 9876543212'
      },
      location: 'Bangalore, India',
      projectDescription: 'Backend API development for mobile app'
    },
    {
      id: 'PL-2024-003',
      companyName: 'Cloud Systems Pvt. Ltd.',
      resourceName: 'Amit Patel',
      resourceId: 8,
      position: 'DevOps Engineer',
      status: 'completed',
      startDate: '2023-10-01',
      endDate: '2024-01-01',
      duration: '3 months',
      monthlyRate: 140000,
      totalValue: 420000,
      companyContact: {
        name: 'Vikram Singh',
        email: 'vikram.singh@cloudsystems.com',
        phone: '+91 9876543213'
      },
      location: 'Delhi, India',
      projectDescription: 'Cloud infrastructure setup and CI/CD pipeline'
    }
  ])

  const activePlacements = placements.filter(p => p.status === 'active')
  const completedPlacements = placements.filter(p => p.status === 'completed')
  const totalEarnings = placements.reduce((sum, p) => sum + (p.status === 'completed' ? p.totalValue : 0), 0)
  const activeEarnings = activePlacements.reduce((sum, p) => sum + p.monthlyRate, 0)

  const getStatusBadge = (status) => {
    return status === 'active' ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300">
        <CheckCircle className="w-4 h-4" />
        Active
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300">
        <CheckCircle className="w-4 h-4" />
        Completed
      </span>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          <p className="text-secondary-600 dark:text-secondary-400">Loading placements...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
            Placements
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            Track your resources placed with companies through AJA Bench
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Active</p>
              <p className="text-2xl font-bold text-success-600">{activePlacements.length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-success-600 dark:text-success-400" />
          </div>
        </div>
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Completed</p>
              <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">{completedPlacements.length}</p>
            </div>
            <Clock className="w-8 h-8 text-secondary-600 dark:text-secondary-400" />
          </div>
        </div>
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Monthly Income</p>
              <p className="text-2xl font-bold text-primary-600">₹{activeEarnings.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
        </div>
        <div className="card p-4 shadow-glow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Total Earned</p>
              <p className="text-2xl font-bold text-success-600">₹{totalEarnings.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-success-600 dark:text-success-400" />
          </div>
        </div>
      </div>

      {/* Active Placements */}
      {activePlacements.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
            Active Placements
          </h2>
          <div className="space-y-4">
            {activePlacements.map((placement, index) => (
              <motion.div
                key={placement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 shadow-glow hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                            {placement.id}
                          </h3>
                          {getStatusBadge(placement.status)}
                        </div>
                        <p className="text-sm text-secondary-600 dark:text-secondary-400">
                          PO: {placement.poId} • {placement.companyName}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-start gap-3">
                        <User className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Resource</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            {placement.resourceName}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Building2 className="w-5 h-5 text-accent-600 dark:text-accent-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Position</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            {placement.position}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Duration</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            {placement.duration} ({new Date(placement.startDate).toLocaleDateString()} - {new Date(placement.endDate).toLocaleDateString()})
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <DollarSign className="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400">Monthly Rate</p>
                          <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                            ₹{placement.monthlyRate.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4 p-3 bg-secondary-50 dark:bg-secondary-800/50 rounded-lg">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">Company Contact</p>
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                          {placement.companyContact.name}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-secondary-600 dark:text-secondary-400">
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {placement.companyContact.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {placement.companyContact.phone}
                          </span>
                        </div>
                        <p className="text-xs text-secondary-600 dark:text-secondary-400 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {placement.location}
                        </p>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-1">Project Description</p>
                      <p className="text-sm text-secondary-700 dark:text-secondary-300">{placement.projectDescription}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 lg:min-w-[200px]">
                    <div className="p-3 bg-success-50 dark:bg-success-900/20 rounded-lg mb-2">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">Total Value</p>
                      <p className="text-lg font-bold text-success-600">₹{placement.totalValue.toLocaleString()}</p>
                    </div>
                    <button className="btn-primary text-sm font-semibold py-2">
                      View Details
                    </button>
                    <button className="btn-outline text-sm font-semibold py-2">
                      View Timesheet
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Completed Placements */}
      {completedPlacements.length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-secondary-900 dark:text-secondary-100 mb-4">
            Completed Placements
          </h2>
          <div className="space-y-4">
            {completedPlacements.map((placement, index) => (
              <motion.div
                key={placement.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="card p-6 shadow-glow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-secondary-900 dark:text-secondary-100">
                        {placement.id}
                      </h3>
                      {getStatusBadge(placement.status)}
                    </div>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-2">
                      {placement.resourceName} • {placement.position} • {placement.companyName}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-secondary-500 dark:text-secondary-400">
                      <span>Duration: {placement.duration}</span>
                      <span>Total Value: ₹{placement.totalValue.toLocaleString()}</span>
                      <span>Completed: {new Date(placement.endDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <button className="btn-outline text-sm font-semibold py-2">
                    View Details
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {placements.length === 0 && (
        <div className="card p-12 text-center">
          <CheckCircle className="w-16 h-16 text-secondary-300 dark:text-secondary-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-2">
            No Placements Yet
          </h3>
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Placements will appear here once resources are successfully placed with companies after interviews.
          </p>
        </div>
      )}
    </div>
  )
}

export default VendorPlacements

